import { useState } from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar,
  BarChart, Bar, XAxis, YAxis, Cell, LabelList,
} from "recharts";
import {
  ShieldCheck, Tag, Layers, Phone, MapPin, Navigation, Star, MessageSquare,
  Megaphone, Image, Clock, Globe, FileText, ListChecks, HelpCircle,
  CheckCircle2, AlertTriangle, XCircle, RotateCcw, CalendarCheck, Mail,
  ChevronDown, Zap, MinusCircle,
} from "lucide-react";
import ScoreGauge from "./ScoreGauge";

const SECTION_META = {
  profile_health: { icon: ShieldCheck, short: "Health" },
  business_name: { icon: Tag, short: "Name" },
  categories: { icon: Layers, short: "Categories" },
  phone_number: { icon: Phone, short: "Phone" },
  nap_consistency: { icon: MapPin, short: "NAP" },
  service_area: { icon: Navigation, short: "Service Area" },
  reviews: { icon: Star, short: "Reviews" },
  review_responses: { icon: MessageSquare, short: "Responses" },
  google_posts: { icon: Megaphone, short: "Posts" },
  photos: { icon: Image, short: "Photos" },
  business_hours: { icon: Clock, short: "Hours" },
  website_link: { icon: Globe, short: "Website" },
  description: { icon: FileText, short: "Description" },
  services_attributes: { icon: ListChecks, short: "Services" },
  questions_answers: { icon: HelpCircle, short: "Q&A" },
};

const colorFor = (score) => (score >= 75 ? "#22C55E" : score >= 50 ? "#FACC15" : "#EF4444");
const STATUS = {
  pass: { color: "#22C55E", Icon: CheckCircle2, label: "Pass" },
  warn: { color: "#FACC15", Icon: AlertTriangle, label: "Needs Work" },
  fail: { color: "#EF4444", Icon: XCircle, label: "Fail" },
  na: { color: "#71717A", Icon: MinusCircle, label: "N/A" },
};
const PRIORITY_COLOR = { High: "#EF4444", Medium: "#FACC15", Low: "#22C55E" };

const StatTile = ({ label, value, color }) => (
  <div className="bg-background border-2 border-zinc-800 p-4 flex flex-col justify-center">
    <div className="font-display text-3xl leading-none" style={{ color }}>{value}</div>
    <div className="font-heading text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{label}</div>
  </div>
);

const SectionTile = ({ section, index }) => {
  const [open, setOpen] = useState(false);
  const st = STATUS[section.status] || STATUS.na;
  const Icon = SECTION_META[section.id]?.icon || ShieldCheck;
  const isNa = section.status === "na" || section.score === null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.03 * index }}
      className="bg-secondary border-2 border-zinc-800"
      data-testid={`audit-section-${section.id}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left p-4 flex items-center gap-3"
        data-testid={`audit-section-toggle-${section.id}`}
      >
        <Icon className="h-5 w-5 shrink-0" style={{ color: st.color }} />
        <div className="flex-1 min-w-0">
          <div className="font-heading text-sm uppercase tracking-wide text-white truncate">{section.title}</div>
          <div className="h-1.5 bg-zinc-800 mt-2 w-full">
            <div className="h-full" style={{ width: `${isNa ? 0 : section.score}%`, background: st.color }} />
          </div>
        </div>
        {isNa ? (
          <span className="font-heading text-xs uppercase tracking-widest shrink-0 text-zinc-500">N/A</span>
        ) : (
          <span className="font-display text-2xl leading-none shrink-0" style={{ color: st.color }}>{section.score}</span>
        )}
        <ChevronDown className={`h-4 w-4 text-zinc-500 shrink-0 transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 fade-up">
          {section.current_value && (
            <div className="font-body text-xs uppercase tracking-wider text-zinc-500 mb-2">{section.current_value}</div>
          )}
          <ul className="space-y-1 mb-3">
            {section.findings?.map((f, i) => (
              <li key={i} className="font-body text-sm text-zinc-300 flex gap-2">
                <span className="mt-1.5 h-1 w-1 shrink-0" style={{ background: st.color }} />{f}
              </li>
            ))}
          </ul>
          {section.recommendation && (
            <div className="border-l-2 border-brand pl-3">
              <span className="font-heading text-[10px] uppercase tracking-widest text-brand">Fix</span>
              <p className="font-body text-sm text-zinc-200 mt-0.5">{section.recommendation}</p>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export const AuditResults = ({ result, emailStatus, onReset }) => {
  const audit = result.audit;
  const sections = audit.sections || [];
  const measurable = sections.filter((s) => s.score !== null && s.status !== "na");
  const notFound = audit.overall_score === null || audit.data_source === "not_found";

  const radarData = measurable.map((s) => ({
    dimension: SECTION_META[s.id]?.short || s.title,
    score: s.score,
  }));
  const barData = [...measurable]
    .sort((a, b) => a.score - b.score)
    .map((s) => ({ name: SECTION_META[s.id]?.short || s.title, score: s.score, color: colorFor(s.score) }));

  const counts = sections.reduce(
    (acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; },
    { pass: 0, warn: 0, fail: 0, na: 0 }
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14" data-testid="audit-results">
      {/* Header: gauge + summary + at-a-glance tiles */}
      <div className="border-2 border-zinc-800 bg-secondary p-8 sm:p-10">
        <div className="flex flex-col lg:flex-row items-center gap-10">
          <ScoreGauge score={audit.overall_score} label={audit.health_label} />
          <div className="flex-1 w-full">
            <span className="font-heading text-xs uppercase tracking-widest text-brand">Google Business Profile Audit</span>
            <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight text-white leading-none mt-2" data-testid="results-business-name">
              {audit.business_name}
            </h1>
            <p className="font-body text-base text-zinc-300 mt-4 max-w-2xl">{audit.summary}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 max-w-xl">
              <StatTile label="Passing" value={counts.pass} color="#22C55E" />
              <StatTile label="Needs Work" value={counts.warn} color="#FACC15" />
              <StatTile label="Failing" value={counts.fail} color="#EF4444" />
              <StatTile label="Not Checkable" value={counts.na} color="#71717A" />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-8">
          <button
            onClick={onReset}
            className="inline-flex items-center gap-2 border-2 border-zinc-700 hover:border-brand text-white font-heading text-sm uppercase tracking-wider px-5 h-11 transition-colors duration-150"
            data-testid="run-another-btn"
          >
            <RotateCcw className="h-4 w-4" /> Run another audit
          </button>
          <span className="inline-flex items-center gap-2 font-body text-xs text-zinc-500">
            <Mail className="h-4 w-4" />
            {emailStatus === "sent" ? "Report emailed to you" : "On-screen report ready"}
          </span>
        </div>
      </div>

      {/* Charts row */}
      {!notFound && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">
        {/* Radar */}
        <div className="border-2 border-zinc-800 bg-secondary p-6" data-testid="radar-chart">
          <h2 className="font-heading text-sm uppercase tracking-widest text-zinc-400 mb-2">Profile Coverage</h2>
          <ResponsiveContainer width="100%" height={320}>
            <RadarChart data={radarData} outerRadius="72%">
              <PolarGrid stroke="#3F3F46" />
              <PolarAngleAxis dataKey="dimension" tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "Oswald" }} />
              <Radar dataKey="score" stroke="#EA580C" strokeWidth={2} fill="#EA580C" fillOpacity={0.4} isAnimationActive={false} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Bar */}
        <div className="border-2 border-zinc-800 bg-secondary p-6" data-testid="bar-chart">
          <h2 className="font-heading text-sm uppercase tracking-widest text-zinc-400 mb-2">Score by Dimension</h2>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={barData} layout="vertical" margin={{ left: 10, right: 24 }}>
              <XAxis type="number" domain={[0, 100]} hide />
              <YAxis type="category" dataKey="name" width={78} tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "Oswald" }} axisLine={false} tickLine={false} />
              <Bar dataKey="score" radius={[0, 2, 2, 0]} barSize={12} isAnimationActive={false}>
                {barData.map((d, i) => <Cell key={i} fill={d.color} />)}
                <LabelList dataKey="score" position="right" fill="#FFFFFF" fontSize={11} fontFamily="Anton" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      )}

      {/* Priority actions */}
      {audit.priority_actions?.length > 0 && (
        <div className="mt-6 border-2 border-brand bg-secondary p-7">
          <h2 className="font-display text-2xl uppercase tracking-tight text-white mb-5 flex items-center gap-2">
            <Zap className="h-6 w-6 text-brand" /> Top Priority Fixes
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {audit.priority_actions.map((a, i) => (
              <div key={i} className="flex items-start gap-3 bg-background border-2 border-zinc-800 p-4" data-testid={`priority-action-${i}`}>
                <span
                  className="font-heading text-[10px] uppercase tracking-wider px-2 py-1 shrink-0"
                  style={{ background: PRIORITY_COLOR[a.priority] || "#71717A", color: "#09090B" }}
                >
                  {a.priority}
                </span>
                <div>
                  <p className="font-body text-sm font-semibold text-white leading-snug">{a.action}</p>
                  <p className="font-body text-xs text-zinc-500 mt-1">{a.impact}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Compact expandable tiles */}
      <h2 className="font-display text-2xl uppercase tracking-tight text-white mt-10 mb-2">Full Breakdown</h2>
      <p className="font-body text-xs text-zinc-500 mb-5">Tap any dimension to see findings and the exact fix.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sections.map((s, i) => <SectionTile key={s.id} section={s} index={i} />)}
      </div>

      {/* CTA */}
      <div className="mt-12 border-2 border-zinc-800 bg-background p-8 sm:p-10 text-center">
        <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tight text-white leading-none">
          Want us to fix all of this for you?
        </h2>
        <p className="font-body text-zinc-400 mt-3 max-w-xl mx-auto">
          We only work with movers. One moving company per service area. We build it, you keep it.
        </p>
        <a
          href="https://calendly.com/moverspro-info/30min"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-6 bg-brand hover:bg-brand-hover text-white font-heading text-lg uppercase tracking-wider px-8 h-14 transition-all duration-150 hover:-translate-y-1 hover:hard-shadow-white"
          data-testid="book-call-btn"
        >
          <CalendarCheck className="h-5 w-5" /> Book a strategy call
        </a>
      </div>
    </div>
  );
};

export default AuditResults;
